import { Browser } from "@capacitor/browser";
import { Clipboard } from '@capacitor/clipboard';
import { Share } from "@capacitor/share";
import { OverlayEventDetail } from "@ionic/core/components";
import {
    IonButton,
    IonContent,
    IonIcon,
    IonPage,
    IonRefresher,
    IonRefresherContent,
    IonSpinner,
    RefresherEventDetail,
    useIonAlert,
    useIonModal,
    useIonToast
} from "@ionic/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    alertCircleOutline,
    briefcaseOutline,
    globeOutline,
    helpCircleOutline,
    linkOutline,
    pencilOutline,
    personAddOutline,
    personRemoveOutline,
    sendOutline
} from "ionicons/icons";
import React, { useContext, useEffect, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router";
import { Events, Params, Screens, useAnalytics } from "../AppAnalyticsProvider";
import { useAppState } from "../AppListenerProvider";
import { AppContext } from "../AppStateProvider";
import { useAuth } from "../AuthProvider";
import BiskProfile from "../components/ComponentsBisk/BiskProfile/BiskProfile";
import CustomHelmet from "../components/ComponentsFunctional/CustomHelmet/CustomHelmet";
import BlockUser from "../components/ComponentsProfile/BlockUser/BlockUser";
import CaseModal from "../components/ComponentsProfile/CaseModal/CaseModal";
import EditProfileModal from "../components/ComponentsProfile/EditProfileModal/EditProfileModal";
import LoadingProfileComponent from "../components/ComponentsProfile/LoadingProfileComponent/LoadingProfileComponent";
import ProfilePicture from "../components/ComponentsProfile/ProfilePicture/ProfilePicture";
import ProfileSocial from "../components/ComponentsProfile/ProfileSocial/ProfileSocial";
import ProfileTrickCard from "../components/ComponentsProfile/ProfileTrickCard/ProfileTrickCard";
import ProfileVirtualGrid from "../components/ComponentsProfile/ProfileVirtualGrid/ProfileVirtualGrid";
import ProfileVirtualSortableGrid from "../components/ComponentsProfile/ProfileVirtualSortableGrid/ProfileVirtualSortableGrid";
import ReturnToProfile from "../components/ComponentsProfile/ReturnToProfile/ReturnToProfile";
import SavedTrickCard from "../components/ComponentsProfile/SavedTrickCard/SavedTrickCard";
import EclipticSearchbar from "../components/ComponentsUI/EclipticSearchbar/EclipticSearchbar";
import IDXButtonIcon from "../components/ComponentsUI/IDXButtonIcon/IDXButtonIcon";
import IDXProfileButton from "../components/ComponentsUI/IDXProfileButton/IDXProfileButton";
import IonSpinnerMainContent from "../components/ComponentsUI/IonSpinnerMainContent/IonSpinnerMainContent";
import ToolbarSearchBarProfile from "../components/ComponentsUI/ToolbarSearchBarProfile/ToolbarSearchBarProfile";
import UsernameDisplay from "../components/ComponentsUI/Username/UsernameDisplay";
import ViewProfileFollowInfoButton from "../components/ComponentsUI/ViewProfileFollowInfoButton/ViewProfileFollowInfoButton";
import useAuthService from "../hooks/useAuthService";
import { getAdjustedTiming, QueryTiming } from "../hooks/useQueryConfig";
import { useRevenueCatPaywall } from "../hooks/useRevenueCatPaywall";
import { Badge } from "../models/badges";
import { ClipType } from "../models/clipType";
import { SportType } from "../models/sportType";
import { User } from "../models/user";
import { UserTrick } from "../models/user-trick";
import { UserTrickSearch } from "../models/user-trick-search";
import { UserPreferencesConstants } from "../models/userPreferencesConstants";
import { UserStreak } from "../models/userstreak";
import { default as badgesService, default as BadgesService } from "../services/badges.service";
import SettingsService from "../services/settings.service";
import TricksService from "../services/tricks.service";
import { default as userService, default as UserService } from "../services/user.service";
import { analyzeUserTrickResForUnlisted, arrangeTricksByPreferences } from "../services/utils";
import { usePhoneVerification } from "../PhoneVerificationProvider";
import LoginButtonPage from "./LoginButtonPage";

interface ProfileParams {
    username?: string;
}

interface ProfileProps extends RouteComponentProps<ProfileParams> { }

const customEmojis = ["👏", "🎉", "🎈", "🫡", "🎊", "😎", "😮‍💨", "😤"]


const Profile: React.FC<ProfileProps> = ({ match }) => {
    const { state, dispatch } = useContext(AppContext);
    const { trackScreen, trackEvent, trackModalView } = useAnalytics();
    const { isNative } = useAppState();
    const history = useHistory();
    const { isAuthenticated, user, isLoading } = useAuth();
    const { login } = useAuthService();
    const { requirePhone } = usePhoneVerification();
    const { username } = match.params;
    const queryClient = useQueryClient();

    //use effect to tricksservice call to getsavedtricks
    const [presentAlert] = useIonAlert();
    const [presentToast] = useIonToast();

    const tabs = ['Clips', 'Lines', 'Edits', 'Filmed', 'Saved'];
    const [activeTab, setActiveTab] = useState(tabs[0]);

    // Set Up user being searched for
    const [profileLoading, setProfileLoading] = useState(false);
    const [userProfileUrl, setUserProfileUrl] = useState(undefined);
    const [editProfile, setEditProfile] = useState(undefined);
    const [profileError, setProfileError] = useState(false);

    const { data: userProfile, isLoading: isLoadingProfile, isError: isErrorProfile, refetch: refetchProfile } = useQuery({
        queryKey: ['userProfile', username],
        queryFn: () => UserService.getUserByUsername(username),
        enabled: !!username,
        ...getAdjustedTiming(QueryTiming.LOW_FREQUENCY)
    });

    const { data: userTricksData, isLoading: userTricksLoading, refetch: refetchUserTricks } = useQuery({
        queryKey: ['userTricks', userProfile?.id],
        queryFn: () => TricksService.getUserTricks(userProfile?.id),
        enabled: !!userProfile?.id,
        ...getAdjustedTiming(QueryTiming.LOW_FREQUENCY)
    });

    const { data: userPreferencesData, refetch: refetchUserPreferences } = useQuery({
        queryKey: ['userPreferences', userProfile?.id],
        queryFn: () => UserService.getUserPreferences(userProfile?.id),
        enabled: !!userProfile?.id,
        ...getAdjustedTiming(QueryTiming.LOW_FREQUENCY)
    });

    const { data: savedVideos, isLoading: savesLoading, refetch: refetchSavedVideos } = useQuery({
        queryKey: ['savedVideos', userProfile?.id],
        queryFn: () => UserService.getUserSavedVideos(userProfile?.id),
        enabled: !!userProfile?.id,
        ...getAdjustedTiming(QueryTiming.LOW_FREQUENCY)
    });

    const { data: filmedVideos, isLoading: filmedLoading, refetch: refetchFilmedVideos } = useQuery({
        queryKey: ['filmedVideos', userProfile?.id],
        queryFn: () => UserService.getUserTaggedVideos(userProfile?.id),
        enabled: !!userProfile?.id,
        ...getAdjustedTiming(QueryTiming.LOW_FREQUENCY)
    });

    const { data: badges, isLoading: badgesLoading, refetch: refetchBadges } = useQuery({
        queryKey: ['userBadges', userProfile?.id],
        queryFn: async () => {
            const badgePromises = userProfile?.profile?.badges.map((badgeId: any) =>
                BadgesService.getBadgeByID(badgeId)
            ) || [];
            console.log("Badge Promises: ", badgePromises);
            return Promise.all(badgePromises);
        },
        enabled: !!userProfile?.id && !!userProfile?.profile?.badges,
        ...getAdjustedTiming(QueryTiming.LOW_FREQUENCY)
    });

    const [tricksLoading, setTricksLoading] = useState(true);

    const [bagOfTricksFilter, setBagOfTricksFilter] = useState<string>("");
    const [userTricks, setUserTricks] = useState([]);
    const [editSortingUserTricks, setEditSortingUserTricks] = useState(false); // deprecated on Native

    const [stashOfLinesFilter, setStashOfLinesFilter] = useState<string>("");
    const [userLines, setUserLines] = useState([]);
    const [editSortingUserLines, setEditSortingUserLines] = useState(false); // deprecated on Native

    const [galleryOfEditsFilter, setGalleryOfEditsFilter] = useState<string>("");
    const [userEdits, setUserEdits] = useState([]);
    const [editSortingUserEdits, setEditSortingUserEdits] = useState(false); // deprecated on Native

    const [claimProfile, setClaimProfile] = useState(false);
    const [followingUser, setFollowingUser] = useState(false);
    const [followingUserLoading, setFollowingUserLoading] = useState(false);

    const [savedVideoFilter, setSavedVideoFilter] = useState<string>("");

    const [filmedVideosFilter, setFilmedVideosFilter] = useState<string>("");

    const [followersCount, setFollowersCount] = useState<number>(0);
    const [followingCount, setFollowingCount] = useState<number>(0);

    const [userBlocked, setUserBlocked] = useState(false);
    const [profileBlocking, setProfileBlocking] = useState(false);

    const {
        presentPaywall: presentTowRopePaywall,
    } = useRevenueCatPaywall({
        userId: state.user.id,
        userBisk: state.bisk,
        username: state.user.username
    });

    const onEditProfileSubmit = async (name: string, username: string, homeMountain: string, sports: string[],
        trickInterests: string[], instagram: string, tiktok: string, youtube: string,
        website: string, website_label: string, discord: string, twitter: string,
        push_notifications: string, setup: string) => {
        const returnedUser: User = await userService.upsertUserProfileInformation(state.user.id,
            name, username, homeMountain, sports, trickInterests, instagram, tiktok, youtube, website, website_label,
            discord, twitter, push_notifications, setup);
        dispatch({ type: 'setUser', payload: returnedUser });
        history.push(`/profile/${returnedUser.username}`);
        await refetchProfile();
    }

    const onClickTrickFromSuggestedTricks = async (trick: string) => {
        history.push(`/search/${trick}`);
    }

    const setUserBadgesFromSorting = async (userBadgesSorted: Badge[]) => {
        const badgeIds = userBadgesSorted.map(badge => badge.id);

        await badgesService.reorderUserBadges(state.user.id, badgeIds);

        // Update the cache
        queryClient.setQueryData(['userBadges', userProfile?.id], userBadgesSorted);

        // Optionally, if the order is also stored in the user profile, update that as well
        queryClient.setQueryData(['userProfile', username], (oldData: User | undefined) => {
            if (!oldData) return oldData;
            return {
                ...oldData,
                profile: {
                    ...oldData.profile,
                    badges: badgeIds
                }
            };
        });
    }

    const shopRedirect = async () => {
        return await presentTowRopePaywall();
    }

    const blockUser = async (blocked_id: string, block: boolean) => {
        // BLOCK_ACTION feeds the moderation / safety panel. Previously a
        // complete blind spot.
        trackEvent(Events.BLOCK_ACTION, {
            [Params.BLOCKED_USER_ID]: blocked_id,
            [Params.ACTION]: block ? 'block' : 'unblock',
        });
        const res = await UserService.blockUser(state.user.id, blocked_id, block);
        setUserBlocked(res);
        const blockedUsers: string[] = await UserService.getBlockedUsers(state.user.id);
        dispatch({ type: 'setBlockedUsers', payload: blockedUsers });
    }

    const reportUser = async (reported_id: string) => {
        // REPORT_ACTION feeds the moderation / safety panel. Report reason
        // isn't captured from this entry point — the ReportReason flow has a
        // richer dropdown; a follow-up can wire report_reason there.
        trackEvent(Events.REPORT_ACTION, {
            [Params.REPORTED_USER_ID]: reported_id,
        });
        const res = await SettingsService.sendFeedback(state.user.id, "Username: " + state.user.username + " wishes to report " + userProfile?.username + " with the id : " + reported_id);
        await presentToastHandler(`${userProfile?.username} has been reported`);
    }

    const getIfBlockedUser = async (user_id: string, blocked_id: string) => {
        const res = await UserService.checkIfBlockedUser(user_id, blocked_id);
        setUserBlocked(res);
    }

    const getIfProfileBlocking = async (user_id: string, blocker_id: string) => {
        const res = await UserService.checkIfBlockingUser(user_id, blocker_id);
        setProfileBlocking(res);
    }

    const goToSelfProfile = (username: string) => {
        history.push(`/profile/${username}`);
    }

    const [presentSocial, dismissSocial] = useIonModal(ProfileSocial, {
        onDismiss: (data: string, role: string) => dismissSocial(data, role),
        userProfileSocial: { ...userProfile },
        displayBlock: !editProfile,
        userBlocked: userBlocked,
        onBlockUser: blockUser,
        onReportUser: reportUser,
        loggedIn: isAuthenticated,
    });

    const [presentTrophy, dismissTrophy] = useIonModal(CaseModal, {
        onDismiss: (data: string, role: string) => dismissTrophy(data, role),
        badges: badges,
        caseTitle: editProfile ? "Your Case" : `${userProfile?.username}'s Case`,
        sports: userProfile?.profile?.sports,
        editProfile: editProfile,
        userProfileId: userProfile?.id,
        isNative: isNative,
        username: userProfile?.username,
        setBadgeOrder: setUserBadgesFromSorting,
        onClickTrickHandler: onClickTrickFromSuggestedTricks,
        shopRedirect: shopRedirect,
    });

    const [presentEditProfileModal, dismissEditProfileModal] = useIonModal(EditProfileModal, {
        onDismiss: (data: string, role: string) => dismissEditProfileModal(data, role),
        onEditProfileSubmit: onEditProfileSubmit,
        userProfileEdit: { ...userProfile }
    });

    const [presentFollowing, dismissFollowing] = useIonModal(ViewProfileFollowInfoButton, {
        onDismiss: (data: string, role: string) => dismissFollowing(data, role),
        userProfileId: userProfile?.id,
        followers: false,
    })

    const [presentFollowers, dismissFollowers] = useIonModal(ViewProfileFollowInfoButton, {
        onDismiss: (data: string, role: string) => dismissFollowers(data, role),
        userProfileId: userProfile?.id,
        followers: true,
    })

    const writeProfileUrlToClipboard = async () => {
        if (isNative) {
            let shareOptions: any = {
                title: "Ecliptic // " + userProfile?.username,
                dialogTitle: "Ecliptic // " + userProfile?.username,
                url: userProfileUrl,
            }
            await Share.share(shareOptions)
        } else {
            await Clipboard.write({
                url: userProfileUrl
            })
            await presentToastHandler("Copied Link to Profile!");
        }
    };

    const resetProfile = async () => {
        console.log("resetting userprofile");
        setProfileError(false);

        setEditSortingUserTricks(false);

        setEditSortingUserLines(false);

        setEditSortingUserEdits(false);

        setBagOfTricksFilter("");
        setStashOfLinesFilter("");
        setGalleryOfEditsFilter("");
        setFilmedVideosFilter("");
        setSavedVideoFilter("");

        setActiveTab(tabs[0]);

        setProfileLoading(false);
        setUserProfileUrl(undefined);
        setFollowersCount(0);
        setFollowingCount(0);
        setClaimProfile(false);
        setEditProfile(undefined);
        setFollowingUser(false);
        setUserBlocked(false);
        setProfileBlocking(false);
    }

    const checkIfFollowingUser = async (loggedInUserId: string, responseId: string) => {
        const isFollowing = await UserService.checkIfUserFollowingOtherUser(loggedInUserId, responseId);
        setFollowingUser(isFollowing);
    }

    const processUserTricks = (tricks: UserTrick[]) => {
        setTricksLoading(true);
        const userTricksFiltered = tricks.filter((userTrick: UserTrick) => userTrick.clip_type === ClipType.CLIP || (userTrick.clip_type !== ClipType.LINE && userTrick.clip_type !== ClipType.EDIT));
        const userLinesRes = tricks.filter((userTrick: UserTrick) => userTrick.clip_type === ClipType.LINE);
        const userEditRes = tricks.filter((userTrick: UserTrick) => userTrick.clip_type === ClipType.EDIT);

        const userTricksToDisplay = analyzeUserTrickResForUnlisted(userTricksFiltered, editProfile);
        const userLinesToDisplay = analyzeUserTrickResForUnlisted(userLinesRes, editProfile);
        const userEditsToDisplay = analyzeUserTrickResForUnlisted(userEditRes, editProfile);

        if (userPreferencesData) {
            const userTrickPreferences: Map<string, number> = userPreferencesData[UserPreferencesConstants.TRICKS] ? new Map(Object.entries(userPreferencesData[UserPreferencesConstants.TRICKS])) : new Map();
            const userLinesPreferences: Map<string, number> = userPreferencesData[UserPreferencesConstants.LINES] ? new Map(Object.entries(userPreferencesData[UserPreferencesConstants.LINES])) : new Map();
            const userEditsPreferences: Map<string, number> = userPreferencesData[UserPreferencesConstants.EDITS] ? new Map(Object.entries(userPreferencesData[UserPreferencesConstants.EDITS])) : new Map();

            if (userTrickPreferences.size > 0) {
                setUserTricks(arrangeTricksByPreferences(userTricksToDisplay, userTrickPreferences));
            } else {
                setUserTricks(userTricksToDisplay);
            }

            if (userLinesPreferences.size > 0) {
                setUserLines(arrangeTricksByPreferences(userLinesToDisplay, userLinesPreferences));
            } else {
                setUserLines(userLinesToDisplay);
            }

            if (userEditsPreferences.size > 0) {
                setUserEdits(arrangeTricksByPreferences(userEditsToDisplay, userEditsPreferences));
            } else {
                setUserEdits(userEditsToDisplay);
            }
        } else {
            setUserTricks(userTricksToDisplay);
            setUserLines(userLinesToDisplay);
            setUserEdits(userEditsToDisplay);
        }
        setTricksLoading(false);
    };


    const getUserProfile = async () => {
        await resetProfile();
        setProfileLoading(true);

        setUserProfileUrl(`${process.env.REACT_APP_IDX_URL}/profile/${userProfile.username}`);

        if (userProfile) {
            const followersCountRes = await UserService.getFollowCount(userProfile.id, true);
            const followingCountRes = await UserService.getFollowCount(userProfile.id, false);
            setFollowersCount(followersCountRes);
            setFollowingCount(followingCountRes);

            setClaimProfile(userProfile.auth_id === null || userProfile.auth_id === undefined || userProfile.auth_id === '');

            let editProfileRes = false;

            if (!isAuthenticated) {
                setEditProfile(false);
            } else {
                if (userProfile.auth_id === user.uid) {
                    setEditProfile(true);
                    editProfileRes = true;
                }

                await checkIfFollowingUser(state.user.id, userProfile.id);
                await getIfBlockedUser(state.user.id, userProfile.id);
                await getIfProfileBlocking(userProfile.id, state.user.id);
            }
        } else {
            setProfileError(true);
        }
        setProfileLoading(false);
        window.prerenderReady = true;
    }

    // Add this useEffect to track profile views
    useEffect(() => {
        if (userProfile && !profileLoading && !isErrorProfile) {
            // Track screen view
            trackScreen(editProfile ? Screens.PROFILE : Screens.PROFILE_OTHER);

            // Track profile view event with metadata
            trackEvent('profile_view', {
                [Params.USER_ID]: userProfile.id,
                profile_username: userProfile.username,
                viewer_id: state.user?.id || 'anonymous',
                is_self_view: editProfile,
                [Params.SPORT_TYPE]: userProfile.profile?.sports?.join(',') || '',
                follower_count: followersCount,
                following_count: followingCount,
                clips_count: userTricks.length
            });
        }
    }, [userProfile, profileLoading, isErrorProfile]);

    useEffect(() => {
        console.log('Processing user tricks', { userTricksDataLength: userTricksData?.length });
        if (userTricksData && userPreferencesData) {
            processUserTricks(userTricksData);
        }
    }, [userTricksData, editProfile, userPreferencesData]);

    useEffect(() => {
        if (!isLoading && isAuthenticated) {
            console.log('match.params:', match.params);
            if (!username && state.user.username) {
                console.log("No username in URL, but user is logged in");
            } else if (username && !isLoadingProfile) {
                getUserProfile();
            } else if (username && isErrorProfile) {
                setProfileError(true);
            }
        }
    }, [isLoading, username, user?.uid, isAuthenticated, isLoadingProfile, isErrorProfile, state.user.id, state.user.username, history, match.params.username]);

    function openSocialModal() {
        trackModalView('profile_social', { [Params.USER_ID]: userProfile?.id });
        presentSocial({
            onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
            },
        });
    }

    function openTrophyModal() {
        trackModalView('profile_trophies', { [Params.USER_ID]: userProfile?.id });
        presentTrophy({
            onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
            },
        });
    }

    function openEditProfileModal() {
        trackModalView('edit_profile');
        presentEditProfileModal({
            onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
            },
        });
    }

    function openFollowingModal() {
        trackModalView('following', { [Params.USER_ID]: userProfile?.id, count: followingCount });
        presentFollowing({
            onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
            },
        });
    }

    function openFollowersModal() {
        trackModalView('followers', { [Params.USER_ID]: userProfile?.id, count: followersCount });
        presentFollowers({
            onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
            },
        });
    }

    const onSocialProfileClick = async () => {
        // Open Modal;
        openSocialModal();
    };

    const onTrophyProfileClick = async () => {
        // Open Modal;
        openTrophyModal();
    }

    const onFollowingProfileClick = async () => {
        // Open Modal;
        openFollowingModal();
    };

    const onFollowersProfileClick = async () => {
        if (!editProfile && !state.isSubscribed && isNative) {
            return await presentTowRopePaywall();
        } else {
            // Open Modal;
            if (editProfile || state.isSubscribed) {
                openFollowersModal();
            } else {
                return;
            }
        }
    };

    const performFollowUser = async (followingUserState: boolean) => {
        setFollowingUserLoading(true);

        const response: User = await UserService.followUserV2(state.user.id, userProfile.id, !followingUserState);
        if (response) {
            setFollowingUserLoading(false);
            setFollowingUser(!followingUserState);

            const followersCountRes = await UserService.getFollowCount(userProfile.id, true);
            const followingCountRes = await UserService.getFollowCount(userProfile.id, false);

            setFollowersCount(followersCountRes);
            setFollowingCount(followingCountRes);

            const followCountForLoggedInUser = await UserService.getFollowCount(state.user.id, false);
            dispatch({ type: 'setFollowing', payload: followCountForLoggedInUser });
        }
    }

    const onFollowUser = async (followingUserState: boolean) => {
        if (isAuthenticated) {
            requirePhone(() => { performFollowUser(followingUserState); });
        } else {
            setFollowingUserLoading(false);
            await presentAlert({
                header: 'Login Required',
                message: 'You must be logged in to follow a user',
                buttons: [
                    'Cancel',
                    {
                        text: 'Login',
                        handler: async () => {
                            await login();
                        }
                    }
                ]
            });
        }
    }

    const onTrickSelection = (trick: UserTrick): void => {
        console.log("CLICKING TRICK");
        history.push('/clip/' + trick.user_id + '/' + trick.trick_id);
    }

    const onResetProfile = async (event: CustomEvent<RefresherEventDetail>) => {
        try {
            await Promise.all([
                refetchProfile(),
                refetchUserTricks(),
                refetchSavedVideos(),
                refetchFilmedVideos(),
                refetchUserPreferences(),
                refetchBadges(),
                queryClient.refetchQueries({ queryKey: ['streak', userProfile?.id] }),
                queryClient.refetchQueries({ queryKey: ['subscription', userProfile?.id] }),
            ]);
        } finally {
            event.detail.complete();
        }
    }

    const goToProfileLink = async () => {
        if (isNative) {
            await Browser.open({
                url: userProfile.profile.website,
                presentationStyle: 'popover',
                'toolbarColor': '#000000'
            });
        } else {
            window.open(userProfile.profile.website, '_blank');
        }
    }

    const handleSortingUserTricks = async (sortableTricks: UserTrick[], segment: string) => {
        if (editProfile) {
            const trickIds: string[] = sortableTricks.map((trick) => trick.trick_id);
            const positions: number[] = Array.from(sortableTricks.entries()).map(([index]) => index);
            await UserService.upsertUserPreferences(user.uid, trickIds, positions, segment).then((response) => {
                if (response) {
                    console.log('Successfully updated user trick preferences');
                    console.log(response);
                }
            });

            queryClient.setQueryData(['userTricks', userProfile?.id], (oldData: UserTrick[] | undefined) => {
                if (!oldData) return sortableTricks;
                return oldData.map(trick => {
                    const updatedTrick = sortableTricks.find(t => t.trick_id === trick.trick_id);
                    return updatedTrick || trick;
                });
            });

            if (segment === UserPreferencesConstants.TRICKS) {
                setUserTricks(sortableTricks);
            } else if (segment === UserPreferencesConstants.LINES) {
                setUserLines(sortableTricks);
            } else if (segment === UserPreferencesConstants.EDITS) {
                setUserEdits(sortableTricks);
            }
        }
    }

    const presentToastHandler = async (message: string) => {
        await presentToast({
            message: message,
            duration: 5000,
            position: 'top',
            icon: alertCircleOutline,
            buttons: [
                {
                    text: 'Dismiss',
                    role: 'cancel',
                    handler: () => {
                        console.log('Dismiss clicked');
                    },
                },
            ],
            onDidDismiss: (e: CustomEvent) => console.log(`Dismissed with role: ${e.detail.role}`)
        });
    };

    // TODO: Maybe move to like a modal or something 
    const presentStreakAlert = async (streakData: UserStreak) => {
        await presentAlert({
            header: `${userProfile?.username}'s Streak`,
            message: `${streakData.streak_length} Week${streakData.streak_length > 1 ? 's' : '' + '! ' + customEmojis[Math.floor(Math.random() * customEmojis.length)]}`,
            buttons: [
                {
                    text: 'OK',
                    handler: () => {
                        console.log('OK clicked');
                    },
                },
            ],
        });
    }

    if (isLoading || profileLoading || tricksLoading || (isAuthenticated && !userProfile && !profileError)) {
        return (
            <IonPage>
                <CustomHelmet
                    title={userProfile?.username ? "Ecliptic // " + userProfile?.username : "Ecliptic // View Profile"}
                    description={userProfile?.username ? `View ${userProfile?.username}'s Bag of Tricks` : "Explore Ecliptic and Follow Skiers and Snowboarders To Create Your Own Feed"}
                    image={userProfile?.profile?.profile_pic || process.env.REACT_APP_DEFAULT_OG_IMAGE || ""}
                    url={`https://ecliptic.day/profile/${username}`} />
                <ToolbarSearchBarProfile username={username} />
                <IonContent>
                    <LoadingProfileComponent />
                </IonContent>
            </IonPage>
        );
    }

    if (!isAuthenticated) {
        return (
            <IonPage>
                <CustomHelmet
                    title={userProfile?.username ? "Ecliptic // " + userProfile?.username : "Ecliptic // View Profile"}
                    description={userProfile?.username ? `View ${userProfile?.username}'s Bag of Tricks` : "Explore Ecliptic and Follow Skiers and Snowboarders To Create Your Own Feed"}
                    image={userProfile?.profile?.profile_pic || process.env.REACT_APP_DEFAULT_OG_IMAGE || ""}
                    url={`https://ecliptic.day/profile/${username}`} />
                <LoginButtonPage
                    title={username ? `Create Your Profile to Build Your Bag of Tricks and View ${username}'s Ecliptic Profile` : "Create Your Profile to Build Your Bag of Tricks and Interact With the Ecliptic Community"}
                    upload={false} profile={true} brand={false} />
            </IonPage>
        )
    }

    if (profileError) {
        return (
            <IonPage>
                <ToolbarSearchBarProfile username={username} />
                <IonContent>
                    <div className="flex flex-col gap-2 m-4 space-y-4 font-bold text-2xl">
                        <div className="flex-col text-center">
                            <div>There was an error loading this profile.</div>
                        </div>
                        <div className="flex-col text-center">
                            <IonButton onClick={() => {
                                history.push('/home');
                            }} color="favorite">Go Back to Ecliptic</IonButton>
                        </div>
                    </div>
                </IonContent>
            </IonPage>
        )
    }

    return (
        <IonPage>
            <CustomHelmet
                title={userProfile?.username ? "Ecliptic // " + userProfile?.username : "Ecliptic // View Profile"}
                description={userProfile?.username ? `View ${userProfile?.username}'s Bag of Tricks` : "Explore Ecliptic and Follow Skiers and Snowboarders To Create Your Own Feed"}
                image={userProfile?.profile?.profile_pic || process.env.REACT_APP_DEFAULT_OG_IMAGE || ""}
                url={`https://ecliptic.day/profile/${username}`} />
            <ToolbarSearchBarProfile username={username} />
            <IonContent>
                <IonRefresher slot="fixed" onIonRefresh={onResetProfile}>
                    <IonRefresherContent></IonRefresherContent>
                </IonRefresher>
                <div className="flex flex-col mx-2 mt-2 mb-0 gap-3 font-bold text-2xl">
                    <div className="grid grid-cols-[auto,1fr,auto] gap-x-4 items-center w-full">
                        {/* Box 1: Pic + Name + Sport + Location */}
                        <div className="flex flex-col items-center">
                            <ProfilePicture src={userProfile?.profile?.profile_pic} editProfile={editProfile}
                                isNative={isNative}></ProfilePicture>
                        </div>
                        <div className="flex flex-col gap-y-0.5">
                            {/* User Info */}
                            <div className="flex flex-row text-xl gap-x-1 items-center">
                                <UsernameDisplay
                                    username={userProfile?.username}
                                    className={""}
                                    onStreakClick={presentStreakAlert}
                                    userId={userProfile?.id}
                                    loggedInId={state.user?.id}
                                    isProfile={true} />
                                {
                                    claimProfile &&
                                    <IDXButtonIcon size="text-2xl" color="dark" icon={helpCircleOutline}
                                        onClick={() => presentToastHandler("Is This You? DM Us on Instagram @idx.style or Email Us at mia@idx.style to claim or remove your profile")} />
                                }
                            </div>
                            <div className="text-base">{userProfile?.name}</div>
                            <div className="text-base">{userProfile?.profile?.home_mountain}</div>
                            <div className="flex flex-row items-center gap-x-1">
                                {/* Website Link - TODO: ADD SPONSORS HERE */}
                                {userProfile?.profile?.website && (
                                    <IonIcon icon={linkOutline} size="small"></IonIcon>)}
                                {userProfile?.profile?.website && (
                                    <div className="flex flex-row gap-2 font-normal text-base"
                                        onClick={goToProfileLink}>
                                        <a>{userProfile?.profile?.website_name || userProfile?.profile?.website}</a>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-col items-end space-y-0.5">
                            <div className="flex flex-col items-end justify-start space-y-0.5">
                                <div className="flex flex-col gap-0.5 items-end">
                                    <IDXProfileButton onClick={onSocialProfileClick} icon={globeOutline}
                                        color="highlight-secondary"
                                        spanText={"Info"} />
                                    <IDXProfileButton onClick={onTrophyProfileClick} icon={briefcaseOutline}
                                        color="theme-secondary"
                                        spanText={"Case"} />
                                    <BiskProfile senderId={state?.user?.id} recipientId={userProfile?.id}
                                        recipientUsername={userProfile?.username}
                                        isAuthed={isAuthenticated} component="profile"
                                        email={state?.user?.email} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-row items-center gap-x-4">
                        <div>
                            {/* Sports Icons */}
                            <div className="flex flex-row items-end gap-x-4">
                                {userProfile?.profile?.sports?.includes(SportType.SNOWBOARDING) && (
                                    <button
                                        className="w-[1.05em] h-[1.05em] rounded-full overflow-hidden flex items-center justify-center">
                                        <span role="img" aria-label="Snowboarding" className="text-lg">🏂</span>
                                    </button>
                                )}
                                {userProfile?.profile?.sports?.includes(SportType.SKIING) && (
                                    <button
                                        className="w-[1.05em] h-[1.05em] rounded-full overflow-hidden flex items-center justify-center">
                                        <span role="img" aria-label="Skiing" className="text-lg">⛷️</span>
                                    </button>
                                )}
                            </div>
                        </div>
                        {badges && !badgesLoading && (
                            <div className="flex flex-row gap-x-4 items-center">
                                {badges.slice(0, 8 - userProfile?.profile?.sports.length).map((badge, index) => (
                                    <button
                                        className="w-[1.05em] h-[1.05em] rounded-full overflow-hidden"
                                        onClick={openTrophyModal}
                                        key={index}
                                    >
                                        <img
                                            src={badge.link}
                                            alt={badge.name}
                                            className="object-cover w-full h-full"
                                        />
                                    </button>
                                ))}
                                {badges.length > (8 - userProfile?.profile?.sports.length) && (
                                    <button className="w-[1.05em] h-[1.05em]" onClick={openTrophyModal}>
                                        <span role="img" aria-label="More Badges" style={{ color: 'white' }}>
                                            …
                                        </span>
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-row mb-3 justify-between items-center flex-nowrap">
                        <div className="flex flex-nowrap">
                            <IonButton fill="solid" size="small" color="favorite"
                                onClick={() => onFollowingProfileClick()}>
                                Following: {followingCount}
                            </IonButton>
                            {
                                <IonButton fill="solid" size="small" color="favorite"
                                    onClick={onFollowersProfileClick}>
                                    Followers: {(editProfile || (state.isSubscribed)) ? followersCount : "???"}
                                </IonButton>
                            }
                        </div>
                        <div className="flex flex-nowrap">
                            <IDXProfileButton onClick={writeProfileUrlToClipboard} icon={sendOutline}
                                spanText={"Share"} />
                            {editProfile ?
                                <IDXProfileButton onClick={openEditProfileModal} icon={pencilOutline}
                                    spanText={"Edit"} fill="solid" color="favorite" /> :
                                followingUserLoading ?
                                    <IonButton fill="clear" size="small">
                                        <IonSpinner name="bubbles" color="theme-alternative"></IonSpinner>
                                    </IonButton> :
                                    (
                                        !followingUser || !isAuthenticated ?
                                            <IDXProfileButton onClick={() => onFollowUser(followingUser)}
                                                icon={personAddOutline}
                                                spanText={"Follow"} fill="solid" color="favorite" /> :
                                            <IDXProfileButton onClick={() => onFollowUser(followingUser)}
                                                icon={personRemoveOutline}
                                                spanText={"Unfollow"} fill="solid" color="favorite" />
                                    )
                            }
                        </div>
                    </div>
                </div>
                {
                    userBlocked || profileBlocking ?
                        <div className="flex flex-row my-2 justify-center">
                            {
                                userBlocked &&
                                <BlockUser blockedUserId={userProfile?.id} userBlocked={userBlocked}
                                    onBlockUser={blockUser} username={userProfile?.username}
                                    loggedIn={isAuthenticated} />
                            }
                            {
                                profileBlocking &&
                                <ReturnToProfile username={state?.user?.username}
                                    usernameCallback={goToSelfProfile} />
                            }
                        </div>
                        :
                        <div>
                            <div className="overflow-x-auto hide-scrollbar tabs-container">
                                <div
                                    className="flex xs:justify-start sm:justify-center space-x-4 min-w-max border-b-2 border-gray-500 text-base font-bold px-2">
                                    {tabs.map((tab, index) => (
                                        <div key={index}
                                            className={activeTab === tab ? 'border-b-4 border-[#5963A7]' : ''}>
                                            <button
                                                key={tab}
                                                className={`py-2 px-2.5 text-xl font-bold  whitespace-nowrap ${activeTab === tab ? 'border-b-4 border-[#5963A7] text-white' : 'text-gray-500 hover:text-white'}`}
                                                onClick={() => {
                                                    setSavedVideoFilter("");
                                                    setBagOfTricksFilter("");
                                                    setStashOfLinesFilter("");
                                                    setGalleryOfEditsFilter("");
                                                    setFilmedVideosFilter("");
                                                    setActiveTab(tab)
                                                }}>
                                                {tab} {!tricksLoading && <span
                                                    className="text-sm">{tab === 'Clips' ? ` ${userTricks.length}` : tab === 'Lines' ? ` ${userLines.length}` : tab === 'Edits' ? ` ${userEdits.length}` : ''}</span>}
                                            </button>
                                        </div>

                                    ))}
                                </div>
                            </div>
                            <div
                                className="flex flex-row justify-between items-center mb-0.5 mx-1">
                                <EclipticSearchbar
                                    onIonInput={(e) => {
                                        if (activeTab === 'Clips') {
                                            setBagOfTricksFilter(e.detail.value)
                                        } else if (activeTab === 'Lines') {
                                            setStashOfLinesFilter(e.detail.value)
                                        } else if (activeTab === 'Edits') {
                                            setGalleryOfEditsFilter(e.detail.value)
                                        } else if (activeTab === 'Filmed') {
                                            setFilmedVideosFilter(e.detail.value)
                                        } else if (activeTab === 'Saved') {
                                            setSavedVideoFilter(e.detail.value)
                                        } else {
                                            return;
                                        }
                                    }}
                                    showClearButton="focus"
                                    showCancelButton="focus"
                                    style={{ paddingTop: 0, paddingBottom: 0 }}
                                    id="searchText"
                                    debounce={0}
                                    animated={true} />
                            </div>
                            <div className="p-1">
                                {
                                    activeTab === 'Clips' &&
                                    <>
                                        {
                                            !tricksLoading ?
                                                (
                                                    userTricks.length > 0 ?
                                                        <>
                                                            <ProfileVirtualSortableGrid<UserTrick>
                                                                key={`${activeTab}-grid-${userTricks.length}`} // Add this key
                                                                items={userTricks}
                                                                setItems={(newState) => {
                                                                    handleSortingUserTricks(newState, UserPreferencesConstants.TRICKS);
                                                                }}
                                                                renderItem={(trick) => (
                                                                    <div className={`${trick.tags.includes("Unlisted") ? "opacity-25" : ""}`}>
                                                                        <ProfileTrickCard trick={trick} />
                                                                    </div>
                                                                )}
                                                                getItemId={(trick) => trick.trick_id}
                                                                filter={(trick) =>
                                                                    trick.trick_name.toLowerCase().includes(bagOfTricksFilter.toLowerCase()) ||
                                                                    (trick.location && trick.location.toLowerCase().includes(bagOfTricksFilter.toLowerCase()))
                                                                }
                                                                disabled={isNative ? (!editSortingUserTricks || bagOfTricksFilter.length > 0 || !editProfile) : (bagOfTricksFilter.length > 0 || !editProfile)}
                                                                isNative={isNative}
                                                                onClick={onTrickSelection}
                                                                activeTab={activeTab}
                                                            />
                                                        </> :
                                                        <div
                                                            className="flex flex-col gap-2 m-4 space-y-4 text-base text-center">
                                                            <div>{editProfile ? "Upload to build your bag of tricks 🤑" : `Looks like ${userProfile?.username}'s Bag of Tricks is empty 😔`}</div>
                                                        </div>
                                                )
                                                :
                                                <div
                                                    className="flex flex-col gap-2 m-4 space-y-4 justify-center items-center">
                                                    <IonSpinnerMainContent className={"my-2"}
                                                        size="medium" /> {/* You can choose other spinner names as well */}
                                                </div>
                                        }
                                    </>
                                }
                                {
                                    activeTab === 'Lines' &&
                                    <>
                                        {
                                            !tricksLoading ?
                                                (
                                                    userLines.length > 0 ?
                                                        <>
                                                            <ProfileVirtualSortableGrid<UserTrick>
                                                                key={`${activeTab}-grid-${userTricks.length}`} // Add this key
                                                                items={userLines}
                                                                setItems={(newState) => {
                                                                    handleSortingUserTricks(newState, UserPreferencesConstants.LINES)
                                                                }}
                                                                renderItem={(trick) => (
                                                                    <div
                                                                        className={trick.tags.includes("Unlisted") ? "opacity-25" : ""}
                                                                    >
                                                                        <ProfileTrickCard trick={trick} />
                                                                    </div>
                                                                )}
                                                                getItemId={(trick) => trick.trick_id}
                                                                filter={(trick) =>
                                                                    trick.trick_name.toLowerCase().includes(stashOfLinesFilter.toLowerCase()) ||
                                                                    (trick.location && trick.location.toLowerCase().includes(stashOfLinesFilter.toLowerCase()))
                                                                }
                                                                disabled={isNative ? (!editSortingUserLines || stashOfLinesFilter.length > 0 || !editProfile) : (stashOfLinesFilter.length > 0 || !editProfile)}
                                                                isNative={isNative}
                                                                onClick={onTrickSelection}
                                                                activeTab={activeTab}
                                                            />
                                                        </> :
                                                        <div
                                                            className="flex flex-col gap-2 m-4 space-y-4 text-base text-center">
                                                            <div>{editProfile ? "Upload to build your stash of lines 😮‍💨" : `Looks like ${userProfile?.username}'s Stash of Lines is empty 😥`}</div>
                                                        </div>
                                                )
                                                :
                                                <div
                                                    className="flex flex-col gap-2 m-4 space-y-4 justify-center items-center">
                                                    <IonSpinnerMainContent className={"my-2"}
                                                        size="medium" /> {/* You can choose other spinner names as well */}

                                                </div>
                                        }
                                    </>
                                }
                                {
                                    activeTab === 'Edits' &&
                                    <>
                                        {
                                            !tricksLoading ?
                                                (
                                                    userEdits.length > 0 ?
                                                        <>
                                                            <ProfileVirtualSortableGrid<UserTrick>
                                                                key={`${activeTab}-grid-${userTricks.length}`} // Add this key
                                                                items={userEdits}
                                                                setItems={(newState) => {
                                                                    handleSortingUserTricks(newState, UserPreferencesConstants.EDITS);
                                                                }}
                                                                renderItem={(trick) => (
                                                                    <div
                                                                        className={trick.tags.includes("Unlisted") ? "opacity-25" : ""}
                                                                    >
                                                                        <ProfileTrickCard trick={trick} />
                                                                    </div>
                                                                )}
                                                                getItemId={(trick) => trick.trick_id}
                                                                filter={(trick) =>
                                                                    trick.trick_name.toLowerCase().includes(galleryOfEditsFilter.toLowerCase()) ||
                                                                    (trick.location && trick.location.toLowerCase().includes(galleryOfEditsFilter.toLowerCase()))
                                                                }
                                                                disabled={isNative ? (!editSortingUserEdits || galleryOfEditsFilter.length > 0 || !editProfile) : (galleryOfEditsFilter.length > 0 || !editProfile)}
                                                                isNative={isNative}
                                                                onClick={onTrickSelection}
                                                                activeTab={activeTab}
                                                            />
                                                        </> :
                                                        <div
                                                            className="flex flex-col gap-2 m-4 space-y-4 text-base text-center">
                                                            <div>{editProfile ? "Upload to build your gallery of edits 🤯" : `Looks like ${userProfile?.username}'s Gallery of Edits is empty 😲`}</div>
                                                        </div>
                                                )
                                                :
                                                <div
                                                    className="flex flex-col gap-2 m-4 space-y-4 justify-center items-center">
                                                    <IonSpinnerMainContent className={"my-2"}
                                                        size="medium" /> {/* You can choose other spinner names as well */}

                                                </div>
                                        }
                                    </>
                                }
                                {activeTab === 'Saved' && (
                                    <>
                                        {!savesLoading ? (
                                            savedVideos.length > 0 ? (
                                                <ProfileVirtualGrid<UserTrickSearch>
                                                    items={savedVideos}
                                                    renderItem={(trick) => <SavedTrickCard trick={trick} />}
                                                    getItemId={(trick) => trick.trick_id}
                                                    filter={(trick) =>
                                                        trick.trick_name.toLowerCase().includes(savedVideoFilter.toLowerCase()) ||
                                                        (trick.location && trick.location.toLowerCase().includes(savedVideoFilter.toLowerCase())) ||
                                                        (trick.username && trick.username.toLowerCase().includes(savedVideoFilter.toLowerCase())) ||
                                                        (trick.name && trick.name.toLowerCase().includes(savedVideoFilter.toLowerCase()))
                                                    }
                                                    onClick={onTrickSelection}
                                                    activeTab={activeTab}
                                                />
                                            ) : (
                                                <div className="flex flex-col gap-2 m-4 space-y-4 text-base text-center">
                                                    <div>{editProfile ? "Save videos to help with your progression 🅿️" : `It appears ${userProfile?.username} hasn't saved any videos 😵‍💫`}</div>
                                                </div>
                                            )
                                        ) : (
                                            <div className="flex flex-col gap-2 m-4 space-y-4 justify-center items-center">
                                                <IonSpinnerMainContent className={"my-2"} size="medium" />
                                            </div>
                                        )}
                                    </>
                                )}
                                {activeTab === 'Filmed' && (
                                    <>
                                        {!filmedLoading ? (
                                            filmedVideos.length > 0 ? (
                                                <ProfileVirtualGrid<UserTrickSearch>
                                                    items={filmedVideos}
                                                    renderItem={(trick) => <SavedTrickCard trick={trick} />}
                                                    getItemId={(trick) => trick.trick_id}
                                                    filter={(trick) =>
                                                        trick.trick_name.toLowerCase().includes(filmedVideosFilter.toLowerCase()) ||
                                                        (trick.location && trick.location.toLowerCase().includes(filmedVideosFilter.toLowerCase())) ||
                                                        (trick.username && trick.username.toLowerCase().includes(filmedVideosFilter.toLowerCase())) ||
                                                        (trick.name && trick.name.toLowerCase().includes(filmedVideosFilter.toLowerCase()))
                                                    } onClick={onTrickSelection}
                                                    activeTab={activeTab}
                                                />
                                            ) : (
                                                <div className="flex flex-col gap-2 m-4 space-y-4 text-base text-center">
                                                    <div>{editProfile ? "Ask the homies to tag you in their clips! 🎥" : `Looks like ${userProfile?.username} hasn't been behind the camera 💡`}</div>
                                                </div>
                                            )
                                        ) : (
                                            <div className="flex flex-col gap-2 m-4 space-y-4 justify-center items-center">
                                                <IonSpinnerMainContent className={"my-2"} size="medium" />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                }
            </IonContent>
        </IonPage>
    );
};

export default Profile;